import { useEffect } from "react"
import { Box, Divider, Grid, Typography, useTheme } from "@mui/material"
import Moment from "react-moment"
import { TextCell2 } from "./components/TextCell"
import IoTInfo from "shared/vehicle/IotInfo"
import { Service } from "./TechnicalData"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { t } from "i18next"
import { useApi } from "auth/useApi3"
import { apiEndpoints } from "generated/apiEndpoints"

const getTotalPassagerare = (data) =>
  (data.antalPassagerare > 0 ? data.antalPassagerare : data.antalSittande) || 0

const getSittandePassagerare = (data) =>
  (data.antalSittande > 0 ? data.antalSittande : data.antalPassagerare) || 0

const getStaendePassagerare = (data) => {
  const total = getTotalPassagerare(data)
  const sittande = getSittandePassagerare(data)
  return total > sittande ? total - sittande : null
}

const BasicInfo = ({ data, vehicleId, tabValue, iotResult, fetchIotData, setTabHistory, tabValueIndex }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  useEffect(() => {
    setTabHistory((prev) => {
      const alreadyVisited = prev.includes(tabValueIndex.BasicInfo)

      if (!alreadyVisited && tabValue === tabValueIndex.BasicInfo) {
        fetchIotData(vehicleId)
      }

      if (!alreadyVisited) {
        return [...prev, tabValueIndex.BasicInfo]
      }
      return prev
    })
  }, [tabValue, vehicleId])

  return (
    <Grid container direction="row" spacing={3}>
      <Grid item md={7} xl={8} xs={12} gap={3} display="flex" flexDirection="column">
        <BasicInfoData data={data} />
        <Divider light />
        <Box>
          <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
            {t("vehicleregistry.control_inspection", "Control inspection")}
          </Typography>
          <Service data={data} />
        </Box>
        <Divider light />
        <Box>
          <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
            {t("global.status", "Status")}
          </Typography>
          <Status t={t} data={data} />
        </Box>
        <Divider light />
        <Box>
          <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
            {t("vehicleregistry.registrationvehicleregistry.registration", "Registration")}
          </Typography>
          <Registration data={data} />
        </Box>
        <Divider light />
        <Box>
          <Typography variant="h3" color={theme.palette.grey[900]} marginBottom={2}>
            {t("vehicleregistry.users", "Users")}
          </Typography>
          <Users data={data} />
        </Box>
      </Grid>

      <Grid item md={5} xl={4} xs={12} gap={2} display="flex" flexDirection="column">
        <IoTInfo vehicleId={vehicleId} iotResult={iotResult} />
        <Box bgcolor="background.light" borderRadius="10px" display="flex" flexDirection="column" p={3}>
          <Grid container direction="row" spacing={2}>
            <Grid item>
              <TextCell2 label={t("vehicleregistry.vehicle_year", "Vehicle year")} text={data.fordonsAr} />
            </Grid>
            {getStaendePassagerare(data) !== null && (
              <Grid item>
                <TextCell2
                  label={t("vehicleregistry.standing_passenger", "Standing passenger")}
                  text={getStaendePassagerare(data) + " " + t("global.pc", "pcs")}
                />
              </Grid>
            )}
            <Grid item>
              <TextCell2
                label={t("vehicleregistry.seated_passanger", "Seated passenger")}
                text={
                  getSittandePassagerare(data) > 0 &&
                  getSittandePassagerare(data) + " " + t("global.pc", "pcs")
                }
              />
            </Grid>
            <Grid item>
              <TextCell2
                label={t("vehicleregistry.total_number_of_passangers", "Total number of passengers")}
                text={
                  getTotalPassagerare(data) > 0 && getTotalPassagerare(data) + " " + t("global.pc", "pcs")
                }
              />
            </Grid>
          </Grid>
        </Box>
        <Box bgcolor="background.light" borderRadius="10px" display="flex" flexDirection="column" p={3}>
          <Grid item>
            <Typography color={theme.palette.grey[900]} variant="h3" mb={2}>
              {t("vehicleregistry.latest_inspection", "Latest inspection")}
            </Typography>
          </Grid>
          <Grid container direction="row" spacing={2}>
            <Grid item>
              <TextCell2
                label={t("global.inspected", "Inspected")}
                text={
                  data.besiktningsdatum && (
                    <Typography variant="bodyMedium" fontWeight={600}>
                      {moment.utc(data.besiktningsdatum).local().format("YYYY-MM-DD")}
                    </Typography>
                  )
                }
              />
            </Grid>
            <Grid item>
              <TextCell2
                label={t("vehicleregistry.inspection_station", "Inspection station")}
                text={data.besiktningsstation}
              />
            </Grid>
            <Grid item>
              <TextCell2
                label={t("vehicleregistry.inspection_program_code", "Inspection program code")}
                text={data.besiktningsprogramkod}
              />
            </Grid>
            <Grid item>
              <TextCell2 label={t("global.meter_setting", "Meter setting")} text={data.matarstallning} />
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  )
}

const BasicInfoData = ({ data }) => {
  return (
    <>
      <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
        <Grid item>
          <TextCell2 label={t("global.table.regnr", "Reg No")} text={data.regNumber} />
        </Grid>
        <Grid item>
          <TextCell2 label={t("global.internal_number_short", "Internal No")} text={data.internnumber} />
        </Grid>
        <Grid item>
          <TextCell2 label={t("global.chassis_number", "Chassis number")} text={data.chassinummer} />
        </Grid>
        <Grid item>
          <TextCell2 label={t("global.paint", "Paint")} text={data.farg} />
        </Grid>
        <Grid item>
          <TextCell2 label={t("global.vehicle_type", "Vehicle type")} text={data.fordonstyp} />
        </Grid>
        <Grid item>
          <TextCell2 label={t("global.environmental_class", "Environmental class")} text={data.miljoklass} />
        </Grid>
        {data.fordonstyp === "Buss" && (
          <Grid item>
            <TextCell2 label={t("vehicleregistry.bus_class", "Bus class")} text={data.bussklass} />
          </Grid>
        )}
        <Grid item>
          <TextCell2
            label={t("vehicleregistry.body", "Body")}
            text={
              <>
                <Typography variant="bodyMedium" fontWeight={600}>
                  {data.karosserikoDriv1}
                </Typography>
                <Typography variant="bodyMedium" fontWeight={600}>
                  {data.karosserikoDriv2}
                </Typography>
              </>
            }
          />
        </Grid>
        <Grid item>
          <TextCell2 label={t("vehicleregistry.make_model", "Make/Model")} text={data.fabrikat} />
        </Grid>
        <Grid item>
          <TextCell2
            label={t("vehicleregistry.vehicle_designation", "Vehicle designation")}
            text={data.fordonsbenamning}
          />
        </Grid>
        <Grid item>
          <TextCell2
            label={t("vehicleregistry.trade_designation", "Trade designation")}
            text={data.handelsbeteckning}
          />
        </Grid>
        <Grid item>
          <TextCell2
            label={t("vehicleregistry.insurance_type", "Insurance type")}
            text={data.forsakringsform}
          />
        </Grid>
        <Grid item>
          <TextCell2 label={t("vehicleregistry.origin_code", "Origin code")} text={data.ursprungskod} />
        </Grid>
      </Grid>
    </>
  )
}

const Status = ({ data }) => {
  return (
    <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
      <Grid item>
        <TextCell2
          label={t("vehicleregistry.vehicle_status", "Vehicle status")}
          text={data.fordonsstatusKod}
        />
      </Grid>
      <Grid item>
        <TextCell2
          label={t("vehicleregistry.date_of_status", "Date of status")}
          text={
            data.fordonsstatusDatum && (
              <Moment date={data.fordonsstatusDatum} format="YYYY-MM-DD" globallocale="sv" />
            )
          }
        />
      </Grid>
      <Grid item>
        <TextCell2 label={t("vehicleregistry.driving_ban", "Driving ban")} text={data.korforbud} />
      </Grid>

      <Grid item>
        <TextCell2
          label={t("vehicleregistry.professional_traffic", "Professional traffic")}
          text={
            <>
              <Typography variant="bodyMedium" fontWeight={600}>
                {data.anvandningssatt1}
              </Typography>
              <Typography variant="bodyMedium" fontWeight={600}>
                {data.anvandningssatt2}
              </Typography>
              <Typography variant="bodyMedium" fontWeight={600}>
                {data.anvandningssatt3}
              </Typography>
              <Typography variant="bodyMedium" fontWeight={600}>
                {data.anvandningssatt4}
              </Typography>
              <Typography variant="bodyMedium" fontWeight={600}>
                {data.anvandningssatt5}
              </Typography>
            </>
          }
        />
      </Grid>
    </Grid>
  )
}

const Registration = ({ data }) => {
  return (
    <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
      <Grid item>
        <TextCell2
          label={t("vehicleregistry.regulatory_inspection_1", "Regulatory inspection (1st)")}
          text={
            data.registreringsdatum && (
              <Moment date={data.registreringsdatum} format="YYYY-MM-DD" globallocale="sv" />
            )
          }
        />
      </Grid>
      <Grid item>
        <TextCell2
          label={t("vehicleregistry.registration_type", "Registration type")}
          text={data.registreringstyp}
        />
      </Grid>
      <Grid item>
        <TextCell2
          label={t("vehicleregistry.regulatory_inspection_requested", "Regulatory inspection (requested)")}
          text={data.korforbud}
        />
      </Grid>
      <Grid item>
        <TextCell2
          label={t("vehicleregistry.in_traffic_for_the_first_time", "In traffic for the first time in SE")}
          text={
            data.registreringsdatum && (
              <Moment date={data.registreringsdatum} format="YYYY-MM-DD" globallocale="sv" />
            )
          }
        />
      </Grid>

      <Grid item>
        <TextCell2
          label={t(
            "vehicleregistry.latest_eu_registration_cert",
            "Latest EU registration certificate part 2"
          )}
          text={data.yrkesmassigTrafik}
        />
      </Grid>
    </Grid>
  )
}

const Users = ({ data }) => {
  return (
    <Grid container direction="row" rowSpacing={2} columnSpacing={4}>
      <Grid item>
        <TextCell2
          label={t("global.org_no", "Org No")}
          text={data.agare && data.agare.substring(0, 6) + "-" + data.agare.substring(6, data.agare.length)}
        />
      </Grid>
      <Grid item>
        <TextCell2
          label={t("vehicleregistry.date_of_acquisition", "Date of acquisition")}
          text={
            data.forvarvsdatum && <Moment date={data.forvarvsdatum} format="YYYY-MM-DD" globallocale="sv" />
          }
        />
      </Grid>
      <Grid item>
        <TextCell2
          label={t("vehicleregistry.users_from", "Users from")}
          text={
            data.forvarvsdatum && <Moment date={data.forvarvsdatum} format="YYYY-MM-DD" globallocale="sv" />
          }
        />
      </Grid>
      <Grid item>
        <TextCell2 label={t("global.address", "Address")} text={data.adress} />
      </Grid>
      <Grid item>
        <TextCell2 label={t("global.postal_code", "ZIP Code")} text={data.postnummer} />
      </Grid>
      <Grid item>
        <TextCell2 label={t("global.postal_address", "Postal address")} text={data.postort} />
      </Grid>
    </Grid>
  )
}

export default BasicInfo
